import React, { useEffect, useState } from 'react'
import {
  Layout,
  Form,
  Button,
  DatePicker,
  Input,
  InputNumber,
  Card,
  Alert,
  Steps,
  Space,
  Row,
  Col,
  Select,
  Spin,
  Divider,
  Tabs,
  Table,
  Badge,
  Tag,
  Modal,
} from 'antd'
import {
  ArrowLeftOutlined,
  CheckCircleOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
} from '@ant-design/icons'
import { useNavigate, useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import dayjs from 'dayjs'
import * as Types from '@/types'
import { apiClient } from '@services/api'
import {
  fetchActivityCodes,
  createOperationActivity,
  deleteOperationActivity,
} from '@/renderer/store/operationsSlice'
import { RootState, AppDispatch } from '@/renderer/store'
import DailyReportHeader from '@/renderer/components/Forms/DailyReportHeader'
import DailyReportFooter from '@/renderer/components/Forms/DailyReportFooter'
import ActivityEntryForm from '@/renderer/components/Forms/ActivityEntryForm'

const { Content } = Layout

const ACTIVITY_CUTOFF_TIME = 6 // 6am default for day splits

export const CreateDailyReport: React.FC = () => {
  const navigate = useNavigate()
  const { operationId, reportId } = useParams()
  const dispatch = useDispatch<AppDispatch>()

  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  const [reportDate, setReportDate] = useState<dayjs.Dayjs>(dayjs())
  const [activities, setActivities] = useState<Types.OperationActivity[]>([])
  const [isActivityModalVisible, setIsActivityModalVisible] = useState(false)
  const [editingActivity, setEditingActivity] = useState<Types.OperationActivity | null>(null)

  const activityCodes = useSelector((state: RootState) => state.operations.activityCodes.items)
  const activityCodesLoading = useSelector((state: RootState) => state.operations.activityCodes.isLoading)

  useEffect(() => {
    // Load activity codes
    dispatch(fetchActivityCodes())
  }, [dispatch])

  // Load existing report if editing
  useEffect(() => {
    if (operationId && reportId) {
      loadExistingReport()
    }
  }, [operationId, reportId])

  const loadExistingReport = async () => {
    try {
      if (!operationId || !reportId) return
      const report = await apiClient.getDailyReportWithActivities(operationId, reportId)
      setActivities(report.activities || [])
      // Auto-sort by start_time on load
      const sorted = [...(report.activities || [])].sort((a, b) => {
        return new Date(a.start_time).getTime() - new Date(b.start_time).getTime()
      })
      setActivities(sorted)
    } catch (error) {
      console.error('Failed to load report', error)
    }
  }

  const handleAddActivity = async (data: Types.OperationActivityCreateRequest) => {
    try {
      setLoading(true)
      if (!operationId || !reportId) return

      const response = await apiClient.createOperationActivity(operationId, reportId, data)
      const newActivities = [...activities, response]
      // Auto-sort by start_time
      newActivities.sort((a, b) => {
        return new Date(a.start_time).getTime() - new Date(b.start_time).getTime()
      })
      setActivities(newActivities)
      setIsActivityModalVisible(false)
      setEditingActivity(null)
    } catch (error: any) {
      console.error('Failed to add activity', error)
      alert(`Error: ${error.message}`)
    } finally {
      setLoading(false)
    }
  }

  const handleDeleteActivity = async (activityId: string) => {
    Modal.confirm({
      title: 'Delete Activity',
      content: 'Are you sure you want to delete this activity?',
      okText: 'Delete',
      okType: 'danger',
      onOk: async () => {
        try {
          if (!operationId || !reportId) return
          await apiClient.deleteOperationActivity(operationId, reportId, activityId)
          setActivities(activities.filter((a) => a.id !== activityId))
        } catch (error: any) {
          alert(`Error: ${error.message}`)
        }
      },
    })
  }

  // Get activities for the selected report date
  const getActivitiesForDate = () => {
    return activities.filter((activity) => {
      const activityDate = dayjs(activity.start_time).format('YYYY-MM-DD')
      const selectedDate = reportDate.format('YYYY-MM-DD')
      return activityDate === selectedDate
    })
  }

  // Calculate totals
  const dateActivities = getActivitiesForDate()
  const calculateTotals = () => {
    const totals = {
      productive: 0,
      npt: 0,
      wait: 0,
    }

    dateActivities.forEach((activity) => {
      const duration = (activity.duration_hours || 0) as number
      if (activity.major_category === 'NPT') {
        totals.npt += duration
      } else if (activity.major_category === 'STDBY') {
        totals.wait += duration
      } else if (activity.major_category === 'Operation') {
        totals.productive += duration
      }
    })

    return totals
  }

  const totals = calculateTotals()

  const activityColumns = [
    {
      title: 'Time',
      key: 'time',
      render: (_: any, record: Types.OperationActivity) => (
        <span>
          {dayjs(record.start_time).format('HH:mm')} - {dayjs(record.end_time).format('HH:mm')}
        </span>
      ),
    },
    {
      title: 'Activity',
      key: 'activity',
      render: (_: any, record: Types.OperationActivity) => (
        <div>
          <div>{record.custom_activity_name || record.sub_activity || 'Custom Activity'}</div>
          <Tag color={getCategoryColor(record.major_category)}>{record.major_category}</Tag>
        </div>
      ),
    },
    {
      title: 'Duration',
      key: 'duration',
      render: (_: any, record: Types.OperationActivity) => `${record.duration_hours?.toFixed(2)} hrs`,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      render: (_: any, record: Types.OperationActivity) => (
        <Space size="small">
          <Button type="text" size="small" icon={<EditOutlined />} />
          <Button
            type="text"
            danger
            size="small"
            icon={<DeleteOutlined />}
            onClick={() => handleDeleteActivity(record.id)}
          />
        </Space>
      ),
    },
  ]

  const getCategoryColor = (category: string) => {
    const colors: Record<string, string> = {
      STDBY: 'blue',
      HSE: 'green',
      NPT: 'red',
      Operation: 'orange',
    }
    return colors[category] || 'default'
  }

  return (
    <Content style={{ padding: '24px' }}>
      <Card>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          {/* Header */}
          <Row justify="space-between" align="middle">
            <Col>
              <h1>{reportId ? 'Edit' : 'Create'} Daily Report</h1>
            </Col>
            <Col>
              <Button
                type="text"
                icon={<ArrowLeftOutlined />}
                onClick={() => navigate('/operations')}
              >
                Back to Operations
              </Button>
            </Col>
          </Row>

          {/* Tabs for sections */}
          <Tabs
            items={[
              {
                key: 'header',
                label: 'Header (Job Info)',
                children: operationId && reportId ? (
                  <DailyReportHeader
                    headerData={{}}
                    onUpdate={async (data) => {
                      // Update via API
                      await apiClient.updateDailyReportHeader(operationId, reportId, data)
                    }}
                  />
                ) : (
                  <Alert message="Please select an operation and report first" type="warning" />
                ),
              },
              {
                key: 'activities',
                label: `Activities (${dateActivities.length})`,
                children: (
                  <Space direction="vertical" style={{ width: '100%' }} size="large">
                    <Row gutter={16}>
                      <Col xs={24} sm={12}>
                        <Form.Item label="Report Date">
                          <DatePicker
                            value={reportDate}
                            onChange={(date) => setReportDate(date || dayjs())}
                            style={{ width: '100%' }}
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12}>
                        <div
                          style={{
                            padding: '8px',
                            backgroundColor: '#f5f5f5',
                            borderRadius: '4px',
                            height: '40px',
                            display: 'flex',
                            alignItems: 'center',
                          }}
                        >
                          <span>
                            Cutoff Time: {ACTIVITY_CUTOFF_TIME}:00am (activities after midnight but before{' '}
                            {ACTIVITY_CUTOFF_TIME}:00am belong to previous day)
                          </span>
                        </div>
                      </Col>
                    </Row>

                    {/* Activity Summary */}
                    <Row gutter={16}>
                      <Col xs={24} sm={8}>
                        <Card size="small">
                          <div>
                            <strong>Productive Hours:</strong>
                            <br />
                            <span style={{ fontSize: '18px', color: '#52c41a' }}>
                              {totals.productive.toFixed(2)}
                            </span>
                          </div>
                        </Card>
                      </Col>
                      <Col xs={24} sm={8}>
                        <Card size="small">
                          <div>
                            <strong>Wait Time:</strong>
                            <br />
                            <span style={{ fontSize: '18px', color: '#1890ff' }}>
                              {totals.wait.toFixed(2)}
                            </span>
                          </div>
                        </Card>
                      </Col>
                      <Col xs={24} sm={8}>
                        <Card size="small">
                          <div>
                            <strong>NPT Hours:</strong>
                            <br />
                            <span style={{ fontSize: '18px', color: '#ff4d4f' }}>
                              {totals.npt.toFixed(2)}
                            </span>
                          </div>
                        </Card>
                      </Col>
                    </Row>

                    <Divider />

                    {/* Activity List */}
                    <div>
                      <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between' }}>
                        <h3>Activities for {reportDate.format('YYYY-MM-DD')}</h3>
                        <Button
                          type="primary"
                          icon={<PlusOutlined />}
                          onClick={() => setIsActivityModalVisible(true)}
                        >
                          Add Activity
                        </Button>
                      </div>

                      {dateActivities.length > 0 ? (
                        <Table
                          dataSource={dateActivities.map((a, idx) => ({ ...a, key: a.id || idx }))}
                          columns={activityColumns}
                          pagination={false}
                          bordered
                          size="small"
                        />
                      ) : (
                        <Alert
                          message="No activities for this date"
                          description="Click 'Add Activity' to create your first activity entry"
                          type="info"
                        />
                      )}
                    </div>

                    {/* Activity Entry Modal */}
                    <Modal
                      title={editingActivity ? 'Edit Activity' : 'Add Activity'}
                      open={isActivityModalVisible}
                      onCancel={() => {
                        setIsActivityModalVisible(false)
                        setEditingActivity(null)
                      }}
                      footer={null}
                      width={800}
                    >
                      <ActivityEntryForm
                        operationId={operationId || ''}
                        reportId={reportId || ''}
                        activityCodes={activityCodes}
                        onSubmit={handleAddActivity}
                        onCancel={() => {
                          setIsActivityModalVisible(false)
                          setEditingActivity(null)
                        }}
                        loading={loading}
                        isEditing={!!editingActivity}
                        initialValues={editingActivity}
                      />
                    </Modal>
                  </Space>
                ),
              },
              {
                key: 'footer',
                label: 'Footer (Equipment & Consumables)',
                children: operationId && reportId ? (
                  <DailyReportFooter
                    footerData={{}}
                    onUpdate={async (data) => {
                      // Update via API
                      await apiClient.updateDailyReportFooter(operationId, reportId, data)
                    }}
                  />
                ) : (
                  <Alert message="Please select an operation and report first" type="warning" />
                ),
              },
            ]}
          />

          {/* Submit */}
          <Space>
            <Button type="primary" size="large" loading={loading}>
              Save Daily Report
            </Button>
            <Button onClick={() => navigate('/operations')}>Cancel</Button>
          </Space>
        </Space>
      </Card>
    </Content>
  )
}

export default CreateDailyReport
